import PolicyLayout from "@/components/PolicyLayout";
import { ShieldCheck, UserX, Camera, MessageCircleWarning, Heart, AlertTriangle } from "lucide-react";

const rules = [
  {
    icon: Heart,
    title: "Respect all attendees, hosts, and venue staff",
    description: "Treat everyone with courtesy and kindness. This is a space for genuine connection, not aggression or ego.",
  },
  {
    icon: UserX,
    title: "No harassment or misconduct",
    description: "No stalking, threatening behavior, unwanted touching, explicit comments, discrimination, or repeated unwanted advances of any kind.",
  },
  {
    icon: ShieldCheck,
    title: "Respect consent and boundaries",
    description: "Always respect personal boundaries. If someone says no or seems uncomfortable, back off immediately. Consent is non-negotiable.",
  },
  {
    icon: Camera,
    title: "No unauthorized recording",
    description: "Do not photograph or record other attendees without their explicit permission. Respect everyone's privacy during and after the event.",
  },
  {
    icon: MessageCircleWarning,
    title: "No pressure for personal details",
    description: "Do not pressure anyone for their phone number, social media handles, or private details. Contact exchange happens through our mutual-matching system only.",
  },
  {
    icon: AlertTriangle,
    title: "Report concerns immediately",
    description: "If you witness or experience any behavior that makes you uncomfortable, report it to the event host or venue staff immediately. We take all reports seriously.",
  },
];

export default function CodeOfConductPage() {
  return (
    <PolicyLayout title="Code of Conduct" subtitle="Our commitment to safety">
      <p className="text-base leading-relaxed text-muted-foreground mb-4">
        <em className="text-sm">Last updated: {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</em>
      </p>

      <p className="text-base leading-relaxed text-muted-foreground mb-10">
        Cita Rush events are spaces for respectful, genuine social connection. Every attendee, host, and venue
        partner is expected to uphold these standards. Violations may result in immediate action.
      </p>

      <div className="grid gap-6 mb-12">
        {rules.map((rule, i) => (
          <div
            key={i}
            className="bg-card border border-border/40 rounded-sm p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-none"
            data-testid={`conduct-rule-${i}`}
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center flex-shrink-0 mt-0.5">
                <rule.icon className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-heading text-lg tracking-tight font-medium mb-2">{rule.title}</h3>
                <p className="text-base leading-relaxed text-muted-foreground">{rule.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border/40 rounded-sm p-6 sm:p-8 mb-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-none">
        <h2 className="font-heading text-xl sm:text-2xl tracking-tight font-medium mb-4">Enforcement</h2>
        <ul className="space-y-2">
          {[
            "Cita Rush may warn, remove, ban, or permanently blacklist attendees for misconduct.",
            "Removal for misconduct does not entitle the participant to any refund.",
            "Repeated offenders may be reported to venue management or local authorities as appropriate.",
            "Cita Rush reserves the right to refuse entry or future registration to anyone who violates this code.",
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-base leading-relaxed text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))] mt-2 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-muted/50 rounded-sm p-6 sm:p-8">
        <h2 className="font-heading text-xl tracking-tight font-medium mb-3">After the event</h2>
        <p className="text-base leading-relaxed text-muted-foreground">
          We encourage post-event meetings only in public places and at personal discretion. Cita Rush is not
          responsible for interactions that occur outside of our events. Please exercise caution and prioritize
          your safety.
        </p>
      </div>

      <div className="mt-10 pt-8 border-t border-border/50">
        <p className="text-sm text-muted-foreground">
          To report a concern or incident, contact us at{" "}
          <span className="text-foreground font-medium">safety@citarush.com</span>.
        </p>
      </div>
    </PolicyLayout>
  );
}
